import React, { useState, useEffect, useCallback, useRef } from 'react';
// import {useNavigate} from "react-router-dom";
import { API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from "utils/tokenManager";

// Components
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import Footer from "components/layout/Footer/Footer";
// import usePreventZoom from "hooks/usePreventZoom";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";

// CSS & Icons
import 'styles/Font.css';
import starIcon from 'assets/images/icon/star-icon.svg';
import defaultCoachImg from 'assets/images/coachSample/coach_ex1.svg';

/*========================================================================*/
// 리뷰 카드 컴포넌트 (수정됨: 아코디언 + 안전한 별점 + 삭제 버튼)
const MyReviewCard = ({ review, onDelete, isExpanded, onToggle }) => {

    // 텍스트 길이 체크 (기준: 60자)
    const contentText = review.cont || '';
    const isLongText = contentText.length > 60;

    const handleToggle = (e) => {
        e.stopPropagation();
        onToggle(); // 부모에게 알림
    };

    return (
        <div style={styles.card}>
            <div style={styles.cardHeader}>
                <div style={styles.coachInfo}>
                    <img
                        src={review.coachImg || defaultCoachImg}
                        alt="코치"
                        style={styles.coachImg}
                    />
                    <div style={styles.coachText}>
                        <span style={styles.coachName}>{review.coachName} 트레이너</span>
                        <span style={styles.branchName}>{review.name || '작성자'}</span>
                    </div>
                </div>

                <div style={styles.headerRight}>
                    <span style={styles.date}>{review.date}</span>
                    {/* 삭제 버튼 유지 */}
                    <button
                        style={styles.deleteButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(review);
                        }}
                    >
                        삭제
                    </button>
                </div>
            </div>

            <div style={staticStyles.divider} />

            <div style={styles.ratingRow}>
                <div style={styles.stars}>
                    <img src={starIcon} alt="별" style={styles.starIcon} />
                    {/* [수정] 별점 안전하게 표시 */}
                    <span style={styles.ratingValue}>
                        {!isNaN(Number(review.rating)) ? Number(review.rating).toFixed(1) : '0.0'}
                    </span>
                </div>
            </div>

            {/* [수정] 내용 영역 (스타일 + 접기/펼치기 적용) */}
            <div style={{
                ...styles.content,
                // 접혀있을 때만 줄 제한 스타일 적용
                ...(!isExpanded ? {
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: '1.6',
                } : {
                    lineHeight: '1.6'
                })
            }}>
                {contentText}
            </div>

            {/* [추가] 더보기 버튼 영역 */}
            {isLongText && (
                <div
                    onClick={handleToggle}
                    style={{
                        marginTop: '4px',
                        display: 'flex',
                        justifyContent: isExpanded ? 'flex-end' : 'flex-start'
                    }}
                >
                    <span style={{
                        color: '#9A9A9A',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'Pretendard',
                    }}>
                        {isExpanded ? '접기' : '... 더보기'}
                    </span>
                </div>
            )}
        </div>
    );
};

function MyReview() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();
    // const navigate = useNavigate();

    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    const [reviews, setReviews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // 삭제 모달 상태
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);

    // 현재 펼쳐진 리뷰의 Key를 저장하는 State
    const [expandedReviewKey, setExpandedReviewKey] = useState(null);

    // 토글 핸들러
    const handleReviewToggle = (key) => {
        setExpandedReviewKey(prevKey => prevKey === key ? null : key);
    };

    // --- API: 내 리뷰 목록 조회 ---
    const fetchMyReviews = useCallback(async () => {
        const REQUEST_URL = `${API_BASE_URL}/review?mbKey=Y`;
        setIsLoading(true);
        try {
            const { token } = getTokenStatus();
            // API 엔드포인트 확인 필요 (예: /myReview)
            const response = await fetch(REQUEST_URL, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error("Network response was not ok");

            const resData = await response.json();
            const data = resData.data || resData;

            let list = data.list || [];
            if (!Array.isArray(list)) list = [list];

            // 최신순 정렬 (date 기준 내림차순)
            // 날짜 형식이 'YYYY-MM-DD HH:mm:ss'라고 가정
            list.sort((a, b) => new Date(b.date) - new Date(a.date));

            setReviews(list);

        } catch (e) {
            console.error("My Reviews Fetch Error:", e);
            // 에러 시 빈 목록 처리 (더미 데이터는 제거하거나 필요 시 유지)
            setReviews([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMyReviews();
    }, [fetchMyReviews]);

    // --- 동적 헤더 높이 측정 ---
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };
        measureHeight();
        window.addEventListener('resize', measureHeight);
        return () => window.removeEventListener('resize', measureHeight);
    }, []);

    // --- [추가] 리뷰 삭제 핸들러 ---
    const handleDeleteClick = (review) => {
        setSelectedReview(review);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedReview) return;

        try {
            const { token } = getTokenStatus();

            // bdKey: 리뷰 목록의 key 값을 사용 (서버 명세에 맞춤)
            const payload = {
                bdKey: selectedReview.key
            };


            const response = await fetch(`${API_BASE_URL}/delReview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const resData = await response.json();

            // 성공 체크 (code 200, '200', '0000' 등)
            if (response.ok && (resData.code === 200 || resData.code === '200' || resData.status === 'success')) {
                // [성공] 목록에서 즉시 제거 (Optimistic Update)
                setReviews(prev => prev.filter(r => r.key !== selectedReview.key));
                // alert("리뷰가 삭제되었습니다."); // 모달 닫히며 자연스럽게 보여주기 위해 생략 가능
            } else {
                alert(`삭제 실패: ${resData.message || "오류가 발생했습니다."}`);
            }

        } catch (e) {
            console.error("Delete Error:", e);
            alert("삭제 중 오류가 발생했습니다.");
        } finally {
            setIsDeleteModalOpen(false);
            setSelectedReview(null);
        }
    };

    const dynamicMainStyle = {
        ...styles.mainContent,
        paddingTop: `${headerHeight}px`,
    };

    return (
        <div style={staticStyles.backgroundWrapper}>
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar} />
                <TopNavigator title="내 리뷰" />
            </div>

            <main style={dynamicMainStyle}>
                {isLoading ? (
                    <div style={styles.emptyState}>로딩 중...</div>
                ) : reviews.length > 0 ? (
                    <div style={styles.listContainer}>
                        {reviews.map((review, index) => {
                            // 고유 키 확보
                            const uniqueKey = review.key || index;
                            return (
                                <MyReviewCard
                                    key={uniqueKey}
                                    review={review}
                                    onDelete={handleDeleteClick}
                                    // 아코디언 상태 전달
                                    isExpanded={expandedReviewKey === uniqueKey}
                                    onToggle={() => handleReviewToggle(uniqueKey)}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div style={styles.emptyState}>작성한 리뷰가 없습니다.</div>
                )}
                {/*<div style={{padding: '0 24px'}}><Footer/></div>*/}
            </main>

            {/* [추가] 삭제 확인 모달 */}
            <ConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="리뷰 삭제"
                message="작성한 리뷰를 삭제하시겠습니까?"
            />
        </div>
    );
}

// 기존 staticStyles 재사용 및 추가 스타일
const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'stretch', margin: '0 auto', boxSizing: 'border-box', overflowX: 'hidden',
    },
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0, width: '100%',
        zIndex: 100, backgroundColor: '#0D042D', display: 'flex',
        flexDirection: 'column',
    },
    statusBar: { flexShrink: 0, backgroundColor: 'transparent' },
    divider: { width: '100%', height: '1px', backgroundColor: '#2C2A40', margin: '12px 0' },
};

const styles = {
    mainContent: {
        // flex: 1, overflowY: 'auto', marginTop: '50px', // 헤더 높이
        flex: 1, overflowY: 'auto',
        display: 'flex', flexDirection: 'column',
        scrollbarWidth: 'none', msOverflowStyle: 'none',
    },
    listContainer: {
        padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px',
    },
    emptyState: {
        color: '#747474', textAlign: 'center', marginTop: '100px', fontSize: '16px', fontFamily: 'Pretendard'
    },
    // 카드 스타일
    card: {
        backgroundColor: '#1B1931', borderRadius: '12px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '8px',
    },
    cardHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    },
    coachInfo: {
        display: 'flex', alignItems: 'center', gap: '12px',
    },
    coachImg: {
        width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', backgroundColor: '#333',
    },
    coachText: {
        display: 'flex', flexDirection: 'column', gap: '2px',
    },
    coachName: {
        color: '#FCFCFA', fontSize: '15px', fontWeight: '600', fontFamily: 'Pretendard',
    },
    branchName: {
        color: '#9A9A9A', fontSize: '12px', fontFamily: 'Pretendard',
    },
    // 헤더 우측 영역 (날짜 + 삭제버튼)
    headerRight: {
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px'
    },
    date: {
        color: '#747474', fontSize: '12px', fontFamily: 'Pretendard',
    },
    ratingRow: {
        display: 'flex', alignItems: 'center', gap: '4px',
    },
    // 삭제 버튼 스타일
    deleteButton: {
        background: 'transparent',
        border: '1px solid #747474',
        borderRadius: '4px',
        color: '#747474',
        fontSize: '11px',
        padding: '2px 6px',
        cursor: 'pointer',
        marginTop: '4px'
    },
    stars: {
        display: 'flex', alignItems: 'center', gap: '4px',
    },
    starIcon: { width: '14px', height: '14px' },
    ratingValue: {
        color: '#FCFCFA', fontSize: '14px', fontWeight: '600', fontFamily: 'Pretendard', marginLeft: '2px',
    },
    content: {
        color: '#B0B0B0', fontSize: '14px', lineHeight: '1.5', fontFamily: 'Pretendard', marginTop: '4px',
        // 긴 단어 강제 줄바꿈 처리
        wordBreak: 'break-all', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', // 줄바꿈(\n) 인식
    }
};

export default MyReview;